import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Building2, Activity, ListChecks, TrendingUp,
  Mail, Calendar, ClipboardList, Sparkles, CalendarClock, Compass,
} from 'lucide-react'
import {
  PageHeader, SectionCard, Card, Button, Badge, HealthBadge, Avatar,
  KpiTile, KpiGrid, EmptyState,
} from '@/components/ui'
import { useStore, useHasPermission } from '@/store/useStore'
import { useCurrentUser } from '@/auth/useCurrentUser'
import { isOwnedBy } from '@/lib/actionOwnership'
import { openM365 } from '@/lib/m365'
import {
  latest, openItemsForAccount, propertiesForAccount, trackById,
} from '@/lib/selectors'
import { money, num, pct, daysUntil, date } from '@/lib/format'
import type {
  Account, ActionItem, KeyDate, Property, Dataset, EntityRef,
  Priority, PropertyHealth, KeyDateStatus,
} from '@/types/entities'
import './Home.css'

/* ---- local helpers (inlined; no shared-file edits) ---- */

const TONE_CLASS: Record<'ok' | 'warn' | 'alert' | 'neutral', string> = {
  ok: 'home-brief-mark--ok',
  warn: 'home-brief-mark--warn',
  alert: 'home-brief-mark--alert',
  neutral: 'home-brief-mark--ok',
}

const PRIORITY_TONE: Record<Priority, 'alert' | 'warn' | 'info' | 'neutral'> = {
  Critical: 'alert', High: 'warn', Medium: 'info', Low: 'neutral',
}

const KEYDATE_TONE: Record<KeyDateStatus, 'alert' | 'warn' | 'info' | 'ok' | 'neutral'> = {
  Overdue: 'alert', 'Due Soon': 'warn', Upcoming: 'info', Met: 'ok', Waived: 'neutral',
}

/** Latest weighted (by NRSF) physical occupancy across a property set. */
function weightedOccupancy(props: Property[]): number | undefined {
  let weighted = 0
  let nrsf = 0
  for (const p of props) {
    const occ = latest(p.occupancyPct)
    const n = p.nrsf ?? 0
    if (occ != null && n > 0) { weighted += occ * n; nrsf += n }
  }
  if (nrsf === 0) {
    // fall back to a simple mean if NRSF is missing
    const vals = props.map((p) => latest(p.occupancyPct)).filter((v): v is number => v != null)
    if (!vals.length) return undefined
    return vals.reduce((s, v) => s + v, 0) / vals.length
  }
  return weighted / nrsf
}

/** Sum of latest monthly in-place revenue across a property set. */
function inPlaceRevenue(props: Property[]): number {
  return props.reduce((s, p) => s + (latest(p.revenue) ?? 0), 0)
}

const ACCOUNT_ROUTE = (id: string) => `/accounts/${id}`

/**
 * Resolve an action-item / key-date reference to its detail route.
 * account -> /accounts/:id, property -> /properties/:id, contact -> /contacts/:id,
 * opportunity -> /opportunities/:id, track -> the track's owning account.
 * Returns undefined when the ref kind/id can't be resolved.
 */
function resolveRefRoute(data: Dataset, ref: EntityRef): string | undefined {
  switch (ref.kind) {
    case 'account':
      return ACCOUNT_ROUTE(ref.id)
    case 'property':
      return `/properties/${ref.id}`
    case 'contact':
      return `/contacts/${ref.id}`
    case 'opportunity':
      return `/opportunities/${ref.id}`
    case 'track': {
      const accountId = trackById(data, ref.id)?.accountId
      return accountId ? ACCOUNT_ROUTE(accountId) : undefined
    }
    default:
      return undefined
  }
}

export default function Home() {
  const data = useStore((s) => s.data)
  const navigate = useNavigate()
  const user = useCurrentUser()
  const canManageAll = useHasPermission('crm.manage')
  const canSeeFinancials = useHasPermission('crm.financials.view')
  const seeAll = canManageAll || canSeeFinancials
  const now = new Date()
  const dayPart = now.getHours() < 12 ? 'morning' : now.getHours() < 17 ? 'afternoon' : 'evening'
  const todayLong = now.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  })

  /* ---- Portfolio-wide aggregates ---- */
  const stats = useMemo(() => {
    const archivedAccounts = new Set(data.accounts.filter((a) => a.archived).map((a) => a.id))
    const props = data.properties.filter(
      (p) => !p.archived && !(p.ownerAccountId && archivedAccounts.has(p.ownerAccountId)),
    )
    const occ = weightedOccupancy(props)
    const revenue = inPlaceRevenue(props)
    const openActionItems = data.actionItems.filter(
      (a) => a.status !== 'Closed' && (seeAll || isOwnedBy(a, user.email, user.displayName)),
    )
    const openItems = openActionItems.length
    const openCriticalHigh = openActionItems.filter(
      (a) => a.priority === 'Critical' || a.priority === 'High',
    ).length
    // Active accounts exclude the internal WPI entity and archived
    // relationships (reconciles with AccountsList).
    const externalAccounts = data.accounts.filter((a) => a.id !== 'acct-wpi' && !a.archived)
    const isProspect = (a: Account) => a.status === 'Prospect' || a.status === 'Suspect'
    const pipelineAccounts = externalAccounts.filter(isProspect).length
    const primaryAccountCount = externalAccounts.length - pipelineAccounts
    return {
      properties: props.length,
      accounts: externalAccounts.length,
      occupancy: occ,
      revenue,
      openItems,
      openCriticalHigh,
      primaryAccountCount,
      pipelineAccounts,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, seeAll, user.email, user.displayName])

  /* ---- Primary relationship accounts: the ones with active relationship
     tracks (this is the "Relationships" section), largest portfolios first.
     Derived, not hardcoded — the prototype pinned seed ids ('acct-jpm', …),
     which broke the day the live sync replaced them with real row ids. ---- */
  const primaryAccounts = useMemo(
    () => {
      const trackedIds = new Set(data.tracks.map((t) => t.accountId))
      return data.accounts
        .filter((a) => a.id !== 'acct-wpi' && !a.archived && trackedIds.has(a.id))
        .sort((a, b) => propertiesForAccount(data, b.id).length - propertiesForAccount(data, a.id).length)
        .slice(0, 4)
        .map((a) => {
          const props = propertiesForAccount(data, a.id)
          return {
            account: a,
            props,
            occupancy: weightedOccupancy(props),
            openItems: openItemsForAccount(data, a.id).length,
            health: a.metrics?.health as PropertyHealth | undefined,
          }
        })
    },
    [data],
  )

  /* ---- Capital pipeline: prospect LPs sourced by Chris Ng ---- */
  const pipeline = useMemo(() => {
    const ids = ['acct-blackstone', 'acct-apollo', 'acct-dell']
    return ids
      .map((id) => data.accounts.find((a) => a.id === id))
      .filter((a): a is Account => Boolean(a))
      .map((a) => {
        const opp = data.opportunities.find(
          (o) => o.principalAccountId === a.id && o.type === 'Equity Partner',
        )
        return { account: a, opp }
      })
  }, [data])

  const pipelineTotal = useMemo(
    () => pipeline.reduce((s, p) => s + (p.opp?.dealSize ?? 0), 0),
    [pipeline],
  )

  /* ---- WISE morning brief: account insights, key-account order first ---- */
  const insights = useMemo(() => {
    const order = new Map(primaryAccounts.map((p, i) => [p.account.id, i]))
    return [...data.accountInsights].sort(
      (a, b) => (order.get(a.accountId) ?? 99) - (order.get(b.accountId) ?? 99),
    )
  }, [data, primaryAccounts])

  /* ---- Critical-date radar (soonest first) ---- */
  const radarDates = useMemo(
    () =>
      [...data.keyDates]
        .sort((a, b) => (a.date < b.date ? -1 : 1))
        .slice(0, 5),
    [data],
  )

  /* ---- Open loops: Critical/High open items, soonest due first ---- */
  const openLoops = useMemo(() => {
    const rank: Record<Priority, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 }
    return data.actionItems
      .filter((a) => a.status !== 'Closed' && (a.priority === 'Critical' || a.priority === 'High')
        && (seeAll || isOwnedBy(a, user.email, user.displayName)))
      .sort((a, b) => {
        const dr = rank[a.priority] - rank[b.priority]
        if (dr !== 0) return dr
        const da = a.dueDate ?? '9999-12-31'
        const db = b.dueDate ?? '9999-12-31'
        return da < db ? -1 : 1
      })
      .slice(0, 5)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, seeAll, user.email, user.displayName])

  const accountName = (id?: string) => data.accounts.find((a) => a.id === id)?.name ?? id ?? '—'

  return (
    <div className="home">
      <PageHeader
        eyebrow="Asset Management"
        title={<>Good {dayPart}, <b>{user.firstName}</b></>}
        sub={`${todayLong} — portfolio holding ${pct(stats.occupancy, 1)} occupancy across ${num(stats.properties)} assets; ${num(stats.openItems)} open items under watch.`}
        actions={
          <div className="home-quick">
            <Button variant="default" size="sm" icon={<Mail size={15} />} onClick={() => openM365('email')}>Email</Button>
            <Button variant="default" size="sm" icon={<Calendar size={15} />} onClick={() => openM365('calendar')}>Calendar</Button>
            <Button variant="default" size="sm" icon={<ClipboardList size={15} />} onClick={() => openM365('planner')}>Planner</Button>
          </div>
        }
      />

      {/* ---- Portfolio stat strip ---- */}
      <KpiGrid cols={5}>
        <KpiTile
          label="Total Properties"
          value={num(stats.properties)}
          sub="across all books"
          onClick={() => navigate('/properties')}
        />
        <KpiTile
          label="Active Accounts"
          value={num(stats.accounts)}
          sub={`${num(stats.primaryAccountCount)} primary, ${num(stats.pipelineAccounts)} pipeline`}
          onClick={() => navigate('/accounts')}
        />
        <KpiTile
          label="Portfolio Occupancy"
          value={stats.occupancy != null ? stats.occupancy.toFixed(1) : '—'}
          unit="%"
          sub="NRSF-weighted, latest"
          onClick={() => navigate('/properties')}
        />
        <KpiTile
          label="In-Place Revenue"
          value={money(stats.revenue, { compact: true })}
          unit="/mo"
          sub="last complete month, all properties"
          onClick={() => navigate('/properties')}
        />
        <KpiTile
          label="Open Action Items"
          value={num(stats.openItems)}
          accent
          sub={`${num(stats.openCriticalHigh)} critical or high`}
          onClick={() => navigate('/tasks')}
        />
      </KpiGrid>

      {/* ---- Primary grid: relationships + WISE brief ---- */}
      <div className="home-split">
        <div className="col" style={{ gap: 'var(--sp-6)' }}>
          {/* Relationship accounts */}
          <SectionCard
            eyebrow="Relationships"
            title={<>Key <b>Accounts</b></>}
            right={<Link to="/accounts"><Button variant="ghost" size="sm">View all</Button></Link>}
          >
            {primaryAccounts.length === 0 ? (
              <EmptyState icon={<Building2 size={26} />} title="No accounts yet" />
            ) : (
              <div className="home-acct-grid">
                {primaryAccounts.map(({ account, props, occupancy, openItems, health }) => (
                  <Card
                    key={account.id}
                    className="home-acct"
                    onClick={() => navigate(ACCOUNT_ROUTE(account.id))}
                  >
                    <div className="home-acct-top">
                      <Avatar name={account.logoText ?? account.name} size={38} tone="navy" />
                      <div className="home-acct-id">
                        <Link
                          to={ACCOUNT_ROUTE(account.id)}
                          className="home-acct-name"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {account.name}
                        </Link>
                        <div className="home-acct-sub">
                          <Badge tone="navy" variant="outline">{account.type}</Badge>
                          <HealthBadge health={health} />
                        </div>
                      </div>
                    </div>
                    <div className="home-acct-stats">
                      <div>
                        <div className="home-stat-label">Assets</div>
                        <div className="home-stat-value">{num(props.length)}</div>
                      </div>
                      <div>
                        <div className="home-stat-label">Occupancy</div>
                        <div className="home-stat-value">
                          {occupancy != null ? occupancy.toFixed(1) : '—'}
                          {occupancy != null && <span className="home-stat-unit">%</span>}
                        </div>
                      </div>
                      <div>
                        <div className="home-stat-label">Open</div>
                        <div className={`home-stat-value${openItems > 0 ? ' home-stat-value--alert' : ''}`}>
                          {num(openItems)}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Capital pipeline */}
          <SectionCard
            eyebrow="Business Development"
            title={<>Capital <b>Pipeline</b></>}
            right={
              <span className="home-pipe-size">
                {money(pipelineTotal, { compact: true })}
                <span>prospective commitment</span>
              </span>
            }
          >
            {pipeline.length === 0 ? (
              <EmptyState icon={<Compass size={26} />} title="No active pipeline" />
            ) : (
              <div className="home-pipeline-list">
                {pipeline.map(({ account, opp }) => (
                  <div className="home-pipe-row" key={account.id}>
                    <Avatar name={account.logoText ?? account.name} size={32} tone="blue" />
                    <div className="home-pipe-main">
                      <Link to={ACCOUNT_ROUTE(account.id)} className="home-pipe-name">
                        {account.name}
                      </Link>
                      <div className="home-pipe-meta">
                        {opp?.salesStage ?? 'Prospect'}
                        {account.sourceReferredBy ? ` · via ${account.sourceReferredBy}` : ''}
                      </div>
                    </div>
                    <Badge tone="info" variant="soft">
                      {opp?.salesType ?? account.status}
                    </Badge>
                    <div className="home-pipe-size">
                      {opp?.dealSize != null ? money(opp.dealSize, { compact: true }) : '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        {/* WISE morning brief (rail) */}
        <SectionCard
          eyebrow="WISE"
          title={<>Morning <b>Brief</b></>}
          right={<Sparkles size={16} color="var(--wpi-cyan)" />}
        >
          {insights.length === 0 ? (
            <EmptyState icon={<Activity size={26} />} title="No insights generated" />
          ) : (
            <div className="home-brief-list">
              {insights.map((ins) => {
                const topDriver =
                  ins.drivers?.find((d) => d.tone === 'alert')
                  ?? ins.drivers?.find((d) => d.tone === 'warn')
                  ?? ins.drivers?.[0]
                const topRec =
                  ins.recommended?.find((r) => r.priority === 'Critical')
                  ?? ins.recommended?.[0]
                const markTone = (topDriver?.tone ?? 'neutral') as keyof typeof TONE_CLASS
                return (
                  <div className="home-brief-row" key={ins.accountId}>
                    <div className={`home-brief-mark ${TONE_CLASS[markTone]}`} />
                    <div>
                      <div className="home-brief-head">
                        <Link to={ACCOUNT_ROUTE(ins.accountId)} className="home-brief-acct">
                          {accountName(ins.accountId)}
                        </Link>
                        <HealthBadge health={ins.verdict} />
                      </div>
                      {topDriver && (
                        <div className="home-brief-line">
                          <b>{topDriver.label}.</b> {topDriver.detail}
                        </div>
                      )}
                      {topRec && (
                        <div className="home-brief-rec">
                          <TrendingUp size={13} color="var(--wpi-blue-60)" style={{ flexShrink: 0, marginTop: 2 }} />
                          <span>
                            <b>{topRec.title}</b>
                            {topRec.suggestedOwner && (
                              <span className="home-brief-owner"> — {topRec.suggestedOwner}</span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </SectionCard>
      </div>

      {/* ---- Critical-date radar + open loops ---- */}
      <SectionCard
        eyebrow="Today's Battlefield"
        title={<>Critical-Date <b>Radar</b> & Open Loops</>}
        right={
          <div className="row row-gap-2">
            <Link to="/calendar"><Button variant="ghost" size="sm" icon={<CalendarClock size={15} />}>Calendar</Button></Link>
            <Link to="/tasks"><Button variant="ghost" size="sm" icon={<ListChecks size={15} />}>Tasks</Button></Link>
          </div>
        }
      >
        <div className="home-split">
          {/* Open loops (left) */}
          <div>
            <div className="home-subhead"><ListChecks size={14} /> Open loops to close</div>
            {openLoops.length === 0 ? (
              <EmptyState icon={<ListChecks size={24} />} title="No high-priority open items" />
            ) : (
              <div className="home-radar">
                {openLoops.map((item) => (
                  <OpenLoopRow key={item.id} item={item} navigate={navigate} data={data} />
                ))}
              </div>
            )}
          </div>

          {/* Critical dates (right) */}
          <div>
            <div className="home-subhead"><CalendarClock size={14} /> Critical dates ahead</div>
            {radarDates.length === 0 ? (
              <EmptyState icon={<Calendar size={24} />} title="No upcoming key dates" />
            ) : (
              <div className="home-radar">
                {radarDates.map((kd) => (
                  <KeyDateRow key={kd.id} kd={kd} />
                ))}
              </div>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  )
}

/* ---- Open-loop row ---- */
function OpenLoopRow({ item, navigate, data }: { item: ActionItem; navigate: (to: string) => void; data: Dataset }) {
  const d = daysUntil(item.dueDate)
  const overdue = d != null && d < 0
  const soon = d != null && d >= 0 && d <= 7
  const ref = item.attachedTo
  const toAccountLink =
    ref.kind === 'account' ? ACCOUNT_ROUTE(ref.id) : undefined
  const openTarget = resolveRefRoute(data, ref) ?? '/tasks'
  return (
    <div className="home-radar-row">
      <div className="home-radar-when">
        {d != null ? (
          <>
            <div className={`home-radar-days${overdue ? ' home-radar-days--overdue' : soon ? ' home-radar-days--soon' : ''}`}>
              {overdue ? Math.abs(d) : d}
            </div>
            <div className="home-radar-dunit">{overdue ? 'days late' : 'days'}</div>
          </>
        ) : (
          <div className="home-radar-dunit">no date</div>
        )}
      </div>
      <div className="home-radar-main">
        <div className="home-radar-title">{item.title}</div>
        <div className="home-radar-sub">
          <Badge tone={PRIORITY_TONE[item.priority]} variant="soft" dot>{item.priority}</Badge>
          {item.owner && <span>{item.owner}</span>}
          {toAccountLink ? (
            <Link to={toAccountLink} onClick={(e) => e.stopPropagation()}>{ref.label}</Link>
          ) : ref.label ? (
            <span className="home-radar-date">{ref.label}</span>
          ) : null}
          {item.dueDate && <span className="home-radar-date">· due {date(item.dueDate, 'short')}</span>}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(openTarget)}
      >
        Open
      </Button>
    </div>
  )
}

/* ---- Key-date radar row ---- */
function KeyDateRow({ kd }: { kd: KeyDate }) {
  const d = daysUntil(kd.date)
  const overdue = d != null && d < 0
  const soon = d != null && d >= 0 && d <= 14
  const ref = kd.attachedTo
  const linkTo = ref.kind === 'account' ? ACCOUNT_ROUTE(ref.id) : undefined
  return (
    <div className="home-radar-row">
      <div className="home-radar-when">
        {d != null ? (
          <>
            <div className={`home-radar-days${overdue ? ' home-radar-days--overdue' : soon ? ' home-radar-days--soon' : ''}`}>
              {overdue ? Math.abs(d) : d}
            </div>
            <div className="home-radar-dunit">{overdue ? 'days late' : 'days'}</div>
          </>
        ) : (
          <div className="home-radar-dunit">—</div>
        )}
      </div>
      <div className="home-radar-main">
        <div className="home-radar-title">{kd.title}</div>
        <div className="home-radar-sub">
          <Badge tone={KEYDATE_TONE[kd.status]} variant="soft" dot>{kd.type}</Badge>
          {linkTo ? (
            <Link to={linkTo}>{ref.label}</Link>
          ) : ref.label ? (
            <span>{ref.label}</span>
          ) : null}
          <span className="home-radar-date">· {date(kd.date, 'med')}</span>
        </div>
      </div>
    </div>
  )
}
